<template>
    <div class="table-qr-panel">
        <div class="header-section">
            <h2 class="mt-5 mb-4">{{ $tl("labels.booking.seatingPlan.qrCode.title") }}</h2>
            <button v-if="isQrCodesTab" type="button" :class="selectionButtonClasses" @click="toggleSelectionMode">
                {{
                    selectionMode ? $tl("labels.booking.seatingPlan.qrCode.cancelSelection") : $tl("labels.booking.seatingPlan.qrCode.selectionMode")
                }}
            </button>
        </div>

        <div class="section-switcher" role="tablist" aria-label="Sections des tables QR codes">
            <button
                v-for="(section, index) in sections"
                :key="section.title"
                type="button"
                :class="['section-switcher__button', { 'section-switcher__button--active': activeTab === index }]"
                :aria-pressed="activeTab === index"
                @click="activeTab = index">
                {{ section.title }}
            </button>
        </div>

        <template v-if="isQrCodesTab">
            <ordering-table-tables-toolbar
                :create-name="createForm.name"
                :create-seats="createForm.seats"
                :create-usage-mode="createForm.usageMode"
                :create-custom-enabled-pax="createForm.customEnabledPax"
                :is-creating="createForm.loading"
                :seating-plans="seatingPlans"
                :selected-seating-plan-id="selectedSeatingPlanId"
                :displayed-tables-count="displayedTables.length"
                :show-create-panel="false"
                :compact-filter-panel="true"
                @update:create-name="createForm.name = $event"
                @update:create-seats="createForm.seats = $event"
                @update:create-usage-mode="createForm.usageMode = $event"
                @update:create-custom-enabled-pax="createForm.customEnabledPax = $event"
                @update:selected-seating-plan-id="selectedSeatingPlanId = $event"
                @create-table="createTable" />

            <div v-if="isLoading" class="state state-loading mb-3">
                <span class="dot"></span>
                Chargement des tables...
            </div>

            <template v-else>
                <div v-if="tables.length === 0" class="alert alert-info">
                    Aucune table ni aucun point d'entrée générique n'est configuré pour cet établissement.
                </div>
                <div v-else-if="displayedTables.length === 0" class="alert alert-info">
                    Aucune table n'est rattachée au plan de salle sélectionné.
                </div>
                <ordering-table-qr-cards-grid
                    v-else
                    :tables="displayedTables"
                    :selection-mode="selectionMode"
                    :selected-table-keys="selectedTables"
                    :statuses="statuses"
                    :widget-statuses="widgetStatuses"
                    @card-click="handleCardClick"
                    @preview="openPreview"
                    @print="printFromTable"
                    @open-widget="openWidget"
                    @test-qr="testQr"
                    @regenerate="confirmRegenerate"
                    @generate="generate" />
            </template>
        </template>

        <template v-else>
            <div class="table-management-notice" :class="managementNoticeClasses">
                <div>
                    <div class="table-management-notice__title">{{ managementNoticeTitle }}</div>
                    <p class="mb-0">{{ managementNoticeBody }}</p>
                </div>
                <button
                    v-if="canOpenSeatingPlan && !isProduction()"
                    type="button"
                    class="btn btn-outline-primary btn-sm btn-circle table-management-notice__action"
                    @click="openSeatingPlan">
                    Ouvrir le plan de salle
                </button>
            </div>

            <ordering-table-tables-toolbar
                :create-name="createForm.name"
                :create-seats="createForm.seats"
                :create-usage-mode="createForm.usageMode"
                :create-custom-enabled-pax="createForm.customEnabledPax"
                :is-creating="createForm.loading"
                :seating-plans="seatingPlans"
                :selected-seating-plan-id="selectedSeatingPlanId"
                :displayed-tables-count="displayedTables.length"
                :show-filter-panel="false"
                @update:create-name="createForm.name = $event"
                @update:create-seats="createForm.seats = $event"
                @update:create-usage-mode="createForm.usageMode = $event"
                @update:create-custom-enabled-pax="createForm.customEnabledPax = $event"
                @update:selected-seating-plan-id="selectedSeatingPlanId = $event"
                @create-table="createTable" />

            <div v-if="isLoading" class="state state-loading mb-3">
                <span class="dot"></span>
                Chargement des tables...
            </div>

            <template v-else>
                <div v-if="tables.length === 0" class="table-management-empty-state">Aucune table n'est encore créée pour cet établissement.</div>
                <div v-else-if="unlinkedTables.length === 0" class="table-management-empty-state">Toutes les tables sont déjà liées à une salle.</div>

                <ordering-table-unlinked-tables
                    :tables="unlinkedTables"
                    :seating-plans="seatingPlans"
                    :room-assignments="roomAssignments"
                    :association-loading-ids="associationLoadingIds"
                    :deletion-loading-ids="deletionLoadingIds"
                    @update-room-assignment="updateRoomAssignment"
                    @associate="associateTable"
                    @delete="deleteTable" />
            </template>
        </template>

        <transition name="slide-up">
            <div v-if="selectionMode && isQrCodesTab && !isQrModalOpen" class="selection-actions-bar shadow-lg">
                <div class="selection-actions-bar__inner">
                    <div class="selection-actions-bar__group">
                        <span class="badge px-3 py-2 me-1 text-small">
                            {{ selectedTables.length }}
                            {{
                                selectedTables.length > 1
                                    ? $tl("labels.booking.seatingPlan.qrCode.selecteds")
                                    : $tl("labels.booking.seatingPlan.qrCode.selected")
                            }}
                        </span>
                        <button type="button" :class="selectAllButtonClasses" :aria-pressed="isAllSelected" @click="selectAll">
                            <i class="fa fa-check-double me-1"></i>
                            {{ $tl("labels.booking.seatingPlan.qrCode.selectAll") }}
                        </button>
                        <button type="button" class="btn btn-sm btn-circle btn-outline-secondary" @click="clearSelection">
                            <i class="fa fa-times me-1"></i>
                            {{ $tl("labels.booking.seatingPlan.qrCode.deselectAll") }}
                        </button>
                    </div>

                    <div v-if="hasBulkActions" class="selection-actions-bar__divider" aria-hidden="true"></div>

                    <div v-if="hasBulkActions" class="selection-actions-bar__group selection-actions-bar__group--actions">
                        <button v-if="canViewSelected" type="button" class="btn btn-outline-dark btn-sm btn-circle" @click="viewSelectedQRCodes">
                            <i class="fa fa-eye me-1"></i>
                            {{ $tl("labels.booking.seatingPlan.qrCode.viewSelected") }}
                        </button>
                        <button
                            v-if="canGenerateSelected"
                            type="button"
                            class="btn btn-primary btn-sm btn-circle"
                            :disabled="isBulkGenerating"
                            @click="generateSelectedQRCodes">
                            <i class="fa fa-qrcode me-1"></i>
                            {{ $tl("labels.booking.seatingPlan.qrCode.generateSelected") }}
                        </button>
                        <button
                            v-if="canRegenerateSelected"
                            type="button"
                            class="btn btn-warning btn-sm btn-circle"
                            :disabled="isBulkGenerating"
                            @click="confirmRegenerateSelected">
                            <i class="fa fa-sync-alt me-1"></i>
                            {{ $tl("labels.booking.seatingPlan.qrCode.regenerateSelected") }}
                        </button>
                        <button v-if="canPrintSelected" type="button" class="btn btn-success btn-sm btn-circle" @click="printSelectedQRCodes">
                            <i class="fa fa-print me-1"></i>
                            {{ $tl("labels.booking.seatingPlan.qrCode.printSelected") }}
                        </button>
                    </div>
                </div>
            </div>
        </transition>

        <modal v-if="preview.visible" @close="closePreviewFromModal">
            <h5 slot="header">{{ $tl("labels.booking.seatingPlan.qrCode.modalTitle", restaurantId, { name: preview.table_name }) }}</h5>
            <div slot="body">
                <div class="text-center">
                    <div v-if="preview.loading" class="py-5">
                        <i class="fa fa-spinner fa-spin fa-2x"></i>
                        <div class="mt-2 text-muted">{{ $tl("labels.booking.seatingPlan.qrCode.generating") }}</div>
                    </div>
                    <div v-else-if="preview.url">
                        <img :src="preview.url" alt="QR" style="max-width: 90%; height: auto" />
                        <div class="mt-3">
                            <button type="button" class="btn btn-outline-dark px-3 py-2 btn-sm flex-fill" @click="download(preview.url)">
                                <i class="fa fa-download"></i>
                            </button>
                        </div>
                    </div>

                    <div v-else class="py-4 text-muted">{{ $tl("labels.booking.seatingPlan.qrCode.noQrAvailable") }}</div>
                </div>
            </div>
            <div slot="footer">
                <button type="button" class="btn btn-secondary btn-circle" @click="closePreview">{{ $tl("labels.form.actions.cancel") }}</button>
            </div>
        </modal>

        <modal v-if="printModal.visible" @close="closePrintModalFromModal" size="lg">
            <h5 slot="header">
                <i class="fa fa-print me-2"></i>
                {{ $tl("labels.booking.seatingPlan.qrCode.printModalTitle") }}
            </h5>
            <div slot="body">
                <div class="mb-4">
                    <p class="text-muted mb-3">
                        <i class="fa fa-info-circle me-1"></i>
                        {{ printModal.tables.length }}
                        {{ $tcl("labels.booking.seatingPlan.qrCode.tablesSelected", printModal.tables.length) }}
                    </p>

                    <div class="mb-3">
                        <label class="form-label fw-bold">{{ $tl("labels.booking.seatingPlan.qrCode.columnsPerLine") }}</label>
                        <div class="d-flex align-items-center" style="gap: 12px">
                            <input
                                v-model.number="printModal.columns"
                                type="range"
                                class="custom-range flex-grow-1"
                                min="1"
                                max="4"
                                style="max-width: 300px" />
                            <span class="badge text-bg-success badge-sm rounded-pill px-3 py-2" style="font-size: 16px; min-width: 50px">
                                {{ printModal.columns }}
                            </span>
                        </div>
                        <small class="form-text d-block text-muted">
                            {{ $tl("labels.booking.seatingPlan.qrCode.columnsHelp") }}
                        </small>
                    </div>
                </div>

                <div class="print-preview-container">
                    <h6 class="fw-bold mb-3">{{ $tl("labels.booking.seatingPlan.qrCode.printPreview") }}</h6>
                    <div class="print-preview border rounded p-3 bg-light">
                        <div class="qr-grid" :style="{ gridTemplateColumns: `repeat(${printModal.columns}, 1fr)` }">
                            <div
                                v-for="table in printModal.tables"
                                :key="`${table.type}-${table.id}`"
                                class="qr-grid-item text-center p-3 bg-white border rounded">
                                <div class="qr-placeholder mb-2">
                                    <i class="fa fa-qrcode fa-3x text-muted"></i>
                                </div>
                                <div class="fw-bold">{{ table.name }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div slot="footer">
                <button type="button" class="btn btn-secondary btn-sm btn-circle me-2" @click="closePrintModal">
                    {{ $tl("labels.form.actions.cancel") }}
                </button>
                <button type="button" class="btn btn-success btn-sm btn-circle" @click="executePrint">
                    <i class="fa fa-print me-2"></i>
                    {{ $tl("labels.booking.seatingPlan.qrCode.print") }}
                </button>
            </div>
        </modal>

        <modal v-if="regenerateConfirm.visible" @close="closeRegenerateConfirmFromModal">
            <h5 slot="header">{{ $tl("labels.booking.seatingPlan.qrCode.confirmRegenerateTitle") }}</h5>
            <div slot="body">
                <div class="alert alert-warning position-relative alerte-position">
                    <i class="fa fa-exclamation-triangle me-2"></i>
                    <strong>{{ $tl("labels.booking.seatingPlan.qrCode.confirmRegenerateWarning") }}</strong>
                    {{ $tl("labels.booking.seatingPlan.qrCode.confirmRegenerateMessage") }}
                </div>
                <p>{{ $tl("labels.booking.seatingPlan.qrCode.confirmRegenerateQuestion", restaurantId, { name: regenerateConfirm.table_name }) }}</p>
            </div>
            <div slot="footer">
                <button type="button" class="btn btn-secondary btn-circle" @click="closeRegenerateConfirm">
                    {{ $tl("labels.form.actions.cancel") }}
                </button>
                <button type="button" class="btn btn-warning btn-circle" @click="regenerate(regenerateConfirm.table)">
                    {{ $tl("labels.booking.seatingPlan.qrCode.regenerate") }}
                </button>
            </div>
        </modal>

        <modal v-if="regenerateSelectedConfirm.visible" @close="closeRegenerateSelectedConfirmFromModal">
            <h5 slot="header">{{ $tl("labels.booking.seatingPlan.qrCode.confirmRegenerateTitle") }}</h5>
            <div slot="body">
                <div class="alert alert-warning position-relative alerte-position">
                    <i class="fa fa-exclamation-triangle me-2"></i>
                    <strong>{{ $tl("labels.booking.seatingPlan.qrCode.confirmRegenerateWarning") }}</strong>
                    {{ $tl("labels.booking.seatingPlan.qrCode.confirmRegenerateMessage") }}
                </div>
                <p>{{ $tl("labels.booking.seatingPlan.qrCode.confirmRegenerateSelectedQuestion") }}</p>
            </div>
            <div slot="footer">
                <button type="button" class="btn btn-secondary btn-circle" @click="closeRegenerateSelectedConfirm">
                    {{ $tl("labels.form.actions.cancel") }}
                </button>
                <button type="button" class="btn btn-warning btn-circle" @click="regenerateSelected">
                    {{ $tl("labels.booking.seatingPlan.qrCode.regenerate") }}
                </button>
            </div>
        </modal>

        <modal v-if="viewSelected.visible" @close="closeViewSelectedFromModal" size="lg">
            <h5 slot="header">{{ $tl("labels.booking.seatingPlan.qrCode.viewSelectedModalTitle") }}</h5>
            <div slot="body">
                <div class="qr-grid" :style="{ gridTemplateColumns: 'repeat(2, 1fr)' }">
                    <div
                        v-for="table in viewSelected.tables"
                        :key="`${table.type}-${table.id}`"
                        class="qr-grid-item text-center p-3 bg-white border rounded">
                        <img :src="table.url" alt="QR" style="max-width: 100%; height: auto" />
                        <div class="fw-bold mt-2">{{ table.name }}</div>
                    </div>
                </div>
            </div>
            <div slot="footer">
                <button type="button" class="btn btn-secondary btn-circle" @click="closeViewSelected">
                    {{ $tl("labels.form.actions.cancel") }}
                </button>
            </div>
        </modal>
    </div>
</template>

<script>
import OrderingTableQrCardsGrid from "./components/OrderingTableQrCardsGrid.vue";
import OrderingTableTablesToolbar from "./components/OrderingTableTablesToolbar.vue";
import OrderingTableUnlinkedTables from "./components/OrderingTableUnlinkedTables.vue";

const createDefaultForm = () => ({
    name: "",
    seats: 6,
    usageMode: "flexible",
    customEnabledPax: [1, 2, 3, 4, 5, 6],
    loading: false,
});

export default {
    name: "OrderingTableConfigurationTables",
    components: {
        OrderingTableQrCardsGrid,
        OrderingTableTablesToolbar,
        OrderingTableUnlinkedTables,
    },
    data() {
        return {
            tables: [],
            seatingPlans: [],
            selectedSeatingPlanId: "",
            isLoading: false,
            activeTab: 0,
            seatingPlanAccessState: "unknown",
            statuses: {},
            widgetStatuses: {},
            selectionMode: false,
            selectedTables: [],
            isBulkGenerating: false,
            bulkGenerationConcurrency: 4,
            bulkGenerationMaxRetries: 3,
            bulkGenerationRetryBaseDelay: 1000,
            roomAssignments: {},
            associationLoadingIds: [],
            deletionLoadingIds: [],
            createForm: createDefaultForm(),
            preview: {
                visible: false,
                table_name: "",
                url: null,
                loading: false,
            },
            regenerateConfirm: {
                visible: false,
                table: null,
                table_name: "",
            },
            printModal: {
                visible: false,
                tables: [],
                columns: 2,
            },
            regenerateSelectedConfirm: {
                visible: false,
            },
            viewSelected: {
                visible: false,
                tables: [],
            },
        };
    },
    computed: {
        restaurantId() {
            return Number.parseInt(this.$route.params.restaurant_id);
        },
        selectionButtonClasses() {
            return ["btn", "btn-sm", "btn-circle", this.selectionMode ? "btn-danger" : "btn-success"];
        },
        isAllSelected() {
            return this.displayedTables.length > 0 && this.displayedTables.every((table) => this.selectedTables.includes(this.tableKey(table)));
        },
        selectAllButtonClasses() {
            return ["btn", "btn-sm", "btn-circle", "me-2", this.isAllSelected ? "btn-secondary active" : "btn-outline-secondary"];
        },
        sections() {
            return [
                {
                    title: "QR Codes",
                },
                {
                    title: "Gérer les tables",
                },
            ];
        },
        isQrCodesTab() {
            return this.activeTab === 0;
        },
        hasSeatingPlanAccess() {
            return this.seatingPlanAccessState === "available";
        },
        hasActiveBooking() {
            return this.$store.getters["restaurants/hasModule"]("booking", this.restaurantId);
        },
        canOpenSeatingPlan() {
            return this.hasSeatingPlanAccess && this.hasActiveBooking;
        },
        hasSeatingPlanRooms() {
            return this.seatingPlans.some((plan) => Array.isArray(plan.rooms) && plan.rooms.length > 0);
        },
        managementNoticeTitle() {
            if (!this.hasSeatingPlanAccess) {
                return "Plan de salle indisponible";
            }

            if (!this.hasActiveBooking) {
                return "Plan de salle en lecture seule";
            }

            if (!this.seatingPlans.length) {
                return "Aucun plan de salle configuré";
            }

            if (!this.hasSeatingPlanRooms) {
                return "Aucune salle configurée pour l'établissement";
            }

            return "Plan de salle partagé avec le module Réservation";
        },
        managementNoticeBody() {
            if (!this.hasSeatingPlanAccess) {
                return "Vous pouvez créer des tables et générer leurs QR codes ici. Pour rattacher ces tables à des salles d'un plan de salle, le module Réservation avec la fonctionnalité Plan de salle est nécessaire.";
            }

            if (!this.hasActiveBooking) {
                return "Les salles existantes peuvent être utilisées pour rattacher les tables. Le module Réservation est nécessaire pour modifier le plan de salle.";
            }

            if (!this.seatingPlans.length) {
                return "Vous pouvez créer des tables et générer leurs QR codes ici. Pour les rattacher à des salles, créez d'abord un plan de salle dans le module Réservation.";
            }

            if (!this.hasSeatingPlanRooms) {
                return "Un plan de salle existe déjà, mais l'établissement n'a encore aucune salle configurée pour rattacher les tables créées ici. Ajoutez d'abord une salle dans le module Réservation.";
            }

            return "Les plans de salle restent gérés dans le module Réservation. Les tables créées ici peuvent ensuite être rattachées à une salle existante.";
        },
        managementNoticeClasses() {
            return {
                "table-management-notice--warning":
                    !this.hasSeatingPlanAccess || !this.hasActiveBooking || !this.seatingPlans.length || !this.hasSeatingPlanRooms,
            };
        },
        displayedTables() {
            return this.tables.filter((table) => {
                if (table.type === "generic") {
                    return true;
                }

                if (this.selectedSeatingPlanId === "") {
                    return true;
                }

                return String(table.seating_plan_id || "") === this.selectedSeatingPlanId;
            });
        },
        unlinkedTables() {
            return this.tables.filter((table) => table.type === "table" && !table.room_id);
        },
        selectedTableObjects() {
            return this.tables.filter((table) => this.selectedTables.includes(this.tableKey(table)));
        },
        selectedTablesWithQr() {
            return this.selectedTableObjects.filter((table) => table.url);
        },
        selectedTablesWithoutQr() {
            return this.selectedTableObjects.filter((table) => !table.url);
        },
        canPrintSelected() {
            return this.selectedTablesWithQr.length > 0;
        },
        canViewSelected() {
            return this.selectedTablesWithQr.length > 0;
        },
        canGenerateSelected() {
            return this.selectedTablesWithoutQr.length > 0;
        },
        canRegenerateSelected() {
            return this.selectedTablesWithQr.length > 0;
        },
        hasBulkActions() {
            return this.canViewSelected || this.canGenerateSelected || this.canRegenerateSelected || this.canPrintSelected;
        },
        isQrModalOpen() {
            return (
                this.preview.visible ||
                this.printModal.visible ||
                this.viewSelected.visible ||
                this.regenerateConfirm.visible ||
                this.regenerateSelectedConfirm.visible
            );
        },
    },
    watch: {
        activeTab(newValue) {
            if (newValue !== 0 && this.selectionMode) {
                this.selectionMode = false;
                this.clearSelection();
            }
        },
        selectedSeatingPlanId() {
            this.clearSelection();
        },
    },
    created() {
        this.loadContext();
    },
    methods: {
        loadContext() {
            this.isLoading = true;

            Promise.all([this.loadTables({ silent: true }), this.loadSeatingPlans()]).finally(() => {
                this.isLoading = false;
            });
        },
        loadTables({ silent = false } = {}) {
            if (!silent) {
                this.isLoading = true;
            }

            return this.httpGet(`/api/ordering-table/restaurants/${this.restaurantId}/tables`)
                .then((response) => {
                    if (response !== false) {
                        this.tables = response.data;
                        this.selectedTables = this.selectedTables.filter((key) => this.tables.some((table) => this.tableKey(table) === key));
                        this.syncRoomAssignments();
                    }
                })
                .finally(() => {
                    if (!silent) {
                        this.isLoading = false;
                    }
                });
        },
        loadSeatingPlans() {
            return this.httpGet(`/api/restaurants/${this.restaurantId}/seatingPlans?include=rooms`, { handleReject: false })
                .then((response) => {
                    if (response === false) {
                        this.seatingPlans = [];
                        this.seatingPlanAccessState = "unavailable";
                        return;
                    }

                    this.seatingPlans = (response.data.data || []).map((plan) => ({
                        ...plan,
                        rooms: (plan.rooms && plan.rooms.data) || [],
                    }));
                    this.seatingPlanAccessState = "available";
                })
                .catch(() => {
                    this.seatingPlans = [];
                    this.seatingPlanAccessState = "unavailable";
                    return false;
                });
        },
        syncRoomAssignments() {
            this.roomAssignments = this.unlinkedTables.reduce((carry, table) => {
                carry[table.id] = this.roomAssignments[table.id] || "";
                return carry;
            }, {});
        },
        createTable() {
            if (this.createForm.name === "" || !this.createForm.seats || this.createForm.seats < 1) {
                this.notifyError(null, "Le nom et le nombre de couverts sont requis.");
                return;
            }

            const enabledPax = this.resolveEnabledPaxForCreateForm();
            if (!enabledPax.length) {
                this.notifyError(null, "Sélectionnez au moins une capacité pour cette table.");
                return;
            }

            this.createForm.loading = true;

            this.httpPost(
                `/api/ordering-table/restaurants/${this.restaurantId}/tables`,
                {
                    name: this.createForm.name,
                    seats: this.createForm.seats,
                    enabled_pax: enabledPax,
                },
                { defaultMessage: "Table créée." }
            )
                .then((response) => {
                    if (response !== false) {
                        this.createForm = { ...createDefaultForm() };
                        this.loadTables();
                    }
                })
                .finally(() => {
                    this.createForm.loading = false;
                });
        },
        resolveEnabledPaxForCreateForm() {
            const seats = Math.max(1, Number(this.createForm.seats) || 0);

            if (this.createForm.usageMode === "strict") {
                return [seats];
            }

            if (this.createForm.usageMode === "standard") {
                return seats <= 1 ? [1] : this.buildPaxRange(2, seats);
            }

            if (this.createForm.usageMode === "custom") {
                return this.createForm.customEnabledPax
                    .map((value) => Number(value))
                    .filter((value) => Number.isInteger(value) && value >= 1 && value <= seats)
                    .sort((left, right) => left - right);
            }

            return this.buildPaxRange(1, seats);
        },
        buildPaxRange(start, end) {
            const safeStart = Math.max(1, Number(start) || 1);
            const safeEnd = Math.max(safeStart, Number(end) || safeStart);

            return Array.from({ length: safeEnd - safeStart + 1 }, (_, index) => safeStart + index);
        },
        updateRoomAssignment({ tableId, roomId }) {
            this.$set(this.roomAssignments, tableId, roomId);
        },
        associateTable(table) {
            const roomId = this.roomAssignments[table.id];
            if (!roomId) {
                return;
            }

            this.associationLoadingIds.push(table.id);

            this.httpPut(
                `/api/ordering-table/restaurants/${this.restaurantId}/tables/${table.id}/room`,
                { room_id: roomId },
                { defaultMessage: "Table associée à la salle." }
            )
                .then((response) => {
                    if (response !== false) {
                        this.$delete(this.roomAssignments, table.id);
                        this.loadTables();
                    }
                })
                .finally(() => {
                    this.associationLoadingIds = this.associationLoadingIds.filter((id) => id !== table.id);
                });
        },
        deleteTable(table) {
            if (!confirm(`Supprimer la table ${table.name} ?`)) {
                return;
            }

            this.deletionLoadingIds.push(table.id);

            this.httpDelete(`/api/ordering-table/restaurants/${this.restaurantId}/tables/${table.id}`, {
                defaultMessage: "Table supprimée.",
            })
                .then((response) => {
                    if (response !== false) {
                        this.$delete(this.roomAssignments, table.id);
                        this.loadTables();
                    }
                })
                .finally(() => {
                    this.deletionLoadingIds = this.deletionLoadingIds.filter((id) => id !== table.id);
                });
        },
        generate(table, isRegenerate = false) {
            this.statuses[table.id] = { generating: true };

            const successKey = isRegenerate
                ? "labels.booking.seatingPlan.qrCode.regenerateSuccess"
                : "labels.booking.seatingPlan.qrCode.generateSuccess";

            this.httpPost(this.getQrEndpoint(table), {}, { defaultMessage: this.$tl(successKey) })
                .then((response) => {
                    if (response !== false) {
                        this.loadTables();
                    }
                })
                .finally(() => {
                    this.statuses[table.id].generating = false;
                });
        },
        confirmRegenerate(table) {
            this.regenerateConfirm = {
                visible: true,
                table,
                table_name: table.name,
            };
        },
        regenerate(table) {
            this.closeRegenerateConfirm();
            this.generate(table, true);
        },
        closeRegenerateConfirm() {
            this.regenerateConfirm = {
                visible: false,
                table: null,
                table_name: "",
            };
        },
        openPreview(table) {
            this.preview.table_name = "";
            this.preview.url = null;
            this.preview.loading = true;
            this.preview.visible = true;

            this.httpGet(this.getQrEndpoint(table))
                .then((response) => {
                    if (response !== false) {
                        this.preview.url = response.data.url;
                        this.preview.table_name = table.name;
                    }
                })
                .finally(() => {
                    this.preview.loading = false;
                });
        },
        closePreview() {
            this.preview.visible = false;
        },
        closePreviewFromModal(event) {
            if (!event.target.classList.contains("modal-wrapper")) {
                event.stopPropagation();
                return;
            }

            this.closePreview();
        },
        closeRegenerateConfirmFromModal(event) {
            if (!event.target.classList.contains("modal-wrapper")) {
                event.stopPropagation();
                return;
            }

            this.closeRegenerateConfirm();
        },
        download(url) {
            const link = document.createElement("a");
            link.href = url;
            link.download = "";
            link.target = "_blank";
            link.click();
        },
        printFromTable(table) {
            this.print(table.url);
        },
        print(url) {
            const styles = `
                @page { size: A4; margin: 20mm; }
                body { margin: 0; padding: 20px; font-family: Arial, sans-serif; text-align: center; }
                img { width: 100%; max-width: 300px; height: auto; display: block; margin: 0 auto; }
                @media print { body { padding: 0; } }
            `;

            this.printDocument("Impression QR Code", styles, `<img src="${url}" />`);
        },
        printDocument(title, styles, bodyHtml) {
            const iframe = document.createElement("iframe");
            iframe.setAttribute("aria-hidden", "true");
            iframe.style.position = "fixed";
            iframe.style.right = "0";
            iframe.style.bottom = "0";
            iframe.style.width = "0";
            iframe.style.height = "0";
            iframe.style.border = "0";
            document.body.appendChild(iframe);

            const removeIframe = () => {
                if (iframe.parentNode) {
                    iframe.parentNode.removeChild(iframe);
                }
            };

            const frameWindow = iframe.contentWindow;
            const frameDocument = frameWindow.document;

            frameDocument.open();
            frameDocument.write(`
                <html>
                    <head>
                        <title>${title}</title>
                        <style>${styles}</style>
                    </head>
                    <body>${bodyHtml}</body>
                </html>
            `);
            frameDocument.close();

            frameWindow.addEventListener("afterprint", removeIframe);

            const triggerPrint = () => {
                frameWindow.focus();
                frameWindow.print();
            };

            const images = Array.from(frameDocument.images);
            const pendingImages = images.filter((image) => !image.complete);

            if (pendingImages.length === 0) {
                triggerPrint();
                return;
            }

            let remaining = pendingImages.length;
            const onImageSettled = () => {
                remaining -= 1;
                if (remaining <= 0) {
                    triggerPrint();
                }
            };

            pendingImages.forEach((image) => {
                image.addEventListener("load", onImageSettled);
                image.addEventListener("error", onImageSettled);
            });
        },
        toggleSelectionMode() {
            this.selectionMode = !this.selectionMode;

            if (!this.selectionMode) {
                this.clearSelection();
            }
        },
        handleCardClick(table) {
            if (!this.selectionMode) {
                return;
            }

            this.toggleTableSelection(this.tableKey(table));
        },
        toggleTableSelection(tableKey) {
            const index = this.selectedTables.indexOf(tableKey);

            if (index > -1) {
                this.selectedTables.splice(index, 1);
                return;
            }

            this.selectedTables.push(tableKey);
        },
        printSelectedQRCodes() {
            this.printModal.tables = this.selectedTablesWithQr;
            this.printModal.visible = true;
        },
        viewSelectedQRCodes() {
            this.viewSelected.tables = this.selectedTablesWithQr;
            this.viewSelected.visible = true;
        },
        closeViewSelected() {
            this.viewSelected.visible = false;
            this.viewSelected.tables = [];
        },
        closeViewSelectedFromModal(event) {
            if (event && event.target && !event.target.classList.contains("modal-wrapper")) {
                event.stopPropagation();
                return;
            }

            this.closeViewSelected();
        },
        generateSelectedQRCodes() {
            this.runBulkGeneration(this.selectedTablesWithoutQr, false);
        },
        confirmRegenerateSelected() {
            this.regenerateSelectedConfirm.visible = true;
        },
        closeRegenerateSelectedConfirm() {
            this.regenerateSelectedConfirm.visible = false;
        },
        closeRegenerateSelectedConfirmFromModal(event) {
            if (event && event.target && !event.target.classList.contains("modal-wrapper")) {
                event.stopPropagation();
                return;
            }

            this.closeRegenerateSelectedConfirm();
        },
        regenerateSelected() {
            this.closeRegenerateSelectedConfirm();
            this.runBulkGeneration(this.selectedTablesWithQr, true);
        },
        runBulkGeneration(tables, isRegenerate) {
            if (tables.length === 0 || this.isBulkGenerating) {
                return;
            }

            this.isBulkGenerating = true;
            tables.forEach((table) => this.$set(this.statuses, table.id, { generating: true }));

            const successKey = isRegenerate
                ? "labels.booking.seatingPlan.qrCode.regenerateSuccess"
                : "labels.booking.seatingPlan.qrCode.generateSuccess";

            this.runWithConcurrencyLimit(tables, (table) => this.generateQrWithRetry(table), this.bulkGenerationConcurrency)
                .then((responses) => {
                    const succeeded = responses.filter((response) => response !== false).length;

                    if (succeeded > 0) {
                        this.notifySuccess(null, this.$tl(successKey));
                    }

                    if (succeeded < responses.length) {
                        this.notifyError(null, this.$tl("labels.booking.seatingPlan.qrCode.bulkGenerationError"));
                    }

                    return this.loadTables({ silent: true });
                })
                .finally(() => {
                    tables.forEach((table) => this.$set(this.statuses, table.id, { generating: false }));
                    this.isBulkGenerating = false;
                });
        },
        runWithConcurrencyLimit(items, worker, limit) {
            const results = new Array(items.length);
            const poolSize = Math.max(1, Math.min(limit, items.length));
            let cursor = 0;

            const runNext = () => {
                const index = cursor;
                cursor += 1;

                if (index >= items.length) {
                    return Promise.resolve();
                }

                return Promise.resolve(worker(items[index], index)).then((result) => {
                    results[index] = result;
                    return runNext();
                });
            };

            return Promise.all(Array.from({ length: poolSize }, () => runNext())).then(() => results);
        },
        generateQrWithRetry(table, attempt = 0) {
            return this.httpPost(this.getQrEndpoint(table), {}, { handleResolve: false, handleReject: false }).catch((error) => {
                const status = error && error.response ? error.response.status : null;

                if (status === 429 && attempt < this.bulkGenerationMaxRetries) {
                    return this.delay(this.resolveRetryDelay(error, attempt)).then(() => this.generateQrWithRetry(table, attempt + 1));
                }

                return false;
            });
        },
        resolveRetryDelay(error, attempt) {
            const header = error && error.response && error.response.headers ? error.response.headers["retry-after"] : null;
            const retryAfterSeconds = Number.parseInt(header, 10);

            if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
                return retryAfterSeconds * 1000;
            }

            return this.bulkGenerationRetryBaseDelay * Math.pow(2, attempt);
        },
        delay(ms) {
            return new Promise((resolve) => setTimeout(resolve, ms));
        },
        closePrintModal() {
            this.printModal.visible = false;
            this.printModal.tables = [];
        },
        closePrintModalFromModal(event) {
            if (event && event.target && !event.target.classList.contains("modal-wrapper")) {
                event.stopPropagation();
                return;
            }

            this.closePrintModal();
        },
        executePrint() {
            const columnWidth = 100 / this.printModal.columns;

            const qrCodesHtml = this.printModal.tables
                .map(
                    (table) => `
                        <div style="
                            width: ${columnWidth}%;
                            float: left;
                            padding: 15px;
                            box-sizing: border-box;
                            text-align: center;
                            page-break-inside: avoid;
                        ">
                            <img src="${table.url}" style="width: 100%; max-width: 200px; height: auto; display: block; margin: 0 auto;" />
                        </div>
                    `
                )
                .join("");

            const styles = `
                @page { size: A4; margin: 20mm; }
                body { margin: 0; padding: 20px; font-family: Arial, sans-serif; }
                .clearfix::after { content: ""; display: table; clear: both; }
                @media print { body { padding: 0; } }
            `;

            this.printDocument("Impression QR Codes", styles, `<div class="clearfix">${qrCodesHtml}</div>`);
            this.closePrintModal();
        },
        selectAll() {
            this.selectedTables = this.displayedTables.map((table) => this.tableKey(table));
        },
        clearSelection() {
            this.selectedTables = [];
        },
        tableKey(table) {
            return `${table.type}:${table.id}`;
        },
        getQrEndpoint(table) {
            const type = table.type === "generic" ? "generic" : "seating_plan_table";
            return `/api/ordering-table/restaurants/${this.restaurantId}/ordering-entry-points/${type}/${table.id}/qr`;
        },
        getWidgetUrlEndpoint(table, mode) {
            const type = table.type === "generic" ? "generic" : "seating_plan_table";
            const endpoint = mode === "qr" ? "qr-test-url" : "widget-url";
            return `/api/ordering-table/${this.restaurantId}/ordering-entry-points/${type}/${table.id}/${endpoint}`;
        },
        openWidget(table) {
            return this.openWidgetLink(table, "preview");
        },
        testQr(table) {
            return this.openWidgetLink(table, "qr");
        },
        openWidgetLink(table, mode) {
            const key = `${this.tableKey(table)}:${mode}`;
            if (this.widgetStatuses[key]) return;
            this.$set(this.widgetStatuses, key, true);
            const widgetWindow = window.open("", "_blank");
            if (widgetWindow) widgetWindow.opener = null;

            return this.httpGet(this.getWidgetUrlEndpoint(table, mode), { handleReject: false })
                .then((response) => {
                    const url = response?.data?.data?.mode === mode ? response.data.data.url : null;
                    if (!url) {
                        if (widgetWindow) {
                            widgetWindow.close();
                        }
                        this.notifyError(null, "Impossible d'ouvrir le widget pour cette table.");
                        return;
                    }

                    if (widgetWindow) {
                        widgetWindow.location = url;
                        return;
                    }

                    window.open(url, "_blank", "noopener,noreferrer");
                })
                .catch((error) => {
                    if (widgetWindow) {
                        widgetWindow.close();
                    }
                    this.notifyError(error);
                })
                .finally(() => {
                    this.$set(this.widgetStatuses, key, false);
                });
        },
        openSeatingPlan() {
            if (!this.canOpenSeatingPlan) {
                return;
            }

            this.$router.push({
                name: "booking.restaurants.settings.seatingPlan",
                params: {
                    restaurant_id: this.restaurantId,
                },
            });
        },
    },
};
</script>

<style scoped lang="scss">
.table-qr-panel {
    padding: 20px 20px 100px 20px;
}

.header-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;

    h2 {
        margin: 0;
    }
}

.section-switcher {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 24px;
    padding: 4px;
    border-radius: 999px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
}

.section-switcher__button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
    padding: 9px 14px;
    border: 1px solid transparent;
    border-radius: 999px;
    background: transparent;
    color: #475569;
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
    text-align: center;
    transition: all 0.2s ease;
}

.section-switcher__button:hover {
    background: rgba(255, 255, 255, 0.9);
}

.section-switcher__button--active {
    background: #ffffff;
    border-color: #cbd5e1;
    color: #0f172a;
    box-shadow: 0 3px 10px rgba(15, 23, 42, 0.08);
}

.table-management-notice {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 20px;
    padding: 16px 18px;
    border: 1px solid #dbeafe;
    border-radius: 14px;
    background: #eff6ff;
    color: #1e3a8a;
}

.table-management-notice--warning {
    border-color: #fde68a;
    background: #fffbeb;
    color: #92400e;
}

.table-management-notice__title {
    margin-bottom: 6px;
    font-size: 15px;
    font-weight: 700;
}

.table-management-notice__action {
    flex: 0 0 auto;
}

.table-management-empty-state {
    margin-bottom: 20px;
    padding: 12px 16px;
    border: 1px solid #bee5eb;
    border-radius: 4px;
    background: #d1ecf1;
    color: #0c5460;
}

.alerte-position {
    top: 0;
}

.selection-actions-bar {
    position: fixed;
    border-radius: 100px;
    bottom: 16px;
    left: 50%;
    transform: translateX(-50%);
    background: #fff;
    padding: 10px 20px;
    width: auto;
    max-width: calc(100% - 32px);
    z-index: 100000000000;
}

.selection-actions-bar__inner {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 10px 16px;
}

.selection-actions-bar__group {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
}

.selection-actions-bar__group--actions {
    justify-content: flex-end;
}

.selection-actions-bar__divider {
    width: 1px;
    align-self: stretch;
    min-height: 24px;
    background: #e2e8f0;
}

.slide-up-enter-active,
.slide-up-leave-active {
    transition: all 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
    transform: translate(-50%, 120%);
    opacity: 0;
}

.print-preview-container {
    .print-preview {
        max-height: 400px;
        overflow-y: auto;
    }

    .qr-grid {
        display: grid;
        gap: 12px;
    }

    .qr-grid-item {
        transition: all 0.2s ease;

        &:hover {
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }
    }

    .qr-placeholder {
        aspect-ratio: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #f8f9fa;
        border-radius: 8px;
    }
}

.qr-grid {
    display: grid;
    gap: 12px;
}

.state {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #4b5563;
    padding: 10px 12px;
    border-radius: 10px;
    background: #e5e7eb;
}

.state-loading {
    background: #e0f2fe;
}

.state .dot {
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: #0284c7;
    animation: pulse 1s infinite;
}

@media (max-width: 991px) {
    .header-section {
        flex-direction: column;
        align-items: stretch;
    }

    .section-switcher {
        width: 100%;
        display: flex;
    }

    .section-switcher__button {
        flex: 1 1 0;
    }

    .table-management-notice {
        flex-direction: column;
        align-items: stretch;
    }

    .table-management-notice__action {
        width: 100%;
    }

    .selection-actions-bar {
        width: calc(100% - 20px);
        max-width: none;
        transform: none;
        left: 10px;
        right: 10px;
        border-radius: 18px;
    }

    .selection-actions-bar__inner {
        flex-direction: column;
        align-items: stretch;
    }

    .selection-actions-bar__group {
        justify-content: center;
    }

    .selection-actions-bar__divider {
        width: 100%;
        min-height: 0;
        height: 1px;
    }

    .slide-up-enter-from,
    .slide-up-leave-to {
        transform: translateY(120%);
    }
}

@keyframes pulse {
    0% {
        transform: scale(0.9);
        opacity: 0.8;
    }
    50% {
        transform: scale(1.1);
        opacity: 1;
    }
    100% {
        transform: scale(0.9);
        opacity: 0.8;
    }
}
</style>
